import type { ReactNode } from 'react';
import type { MenuItemChildProps } from '../components/Sidebar/SectionMenuItemChild';

import { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router';
import { twMerge } from 'tailwind-merge';

import { manageSubscription } from '../lib/flotiq-client/api-helpers';
import { getStripeURL } from '../lib/helpers';

import useDarkMode from '../hooks/useDarkMode';
import useToken from '../hooks/useToken';

import UserContext from '../contexts/UserContext';

import {
  ArrowLeftIcon,
  CardPaymentIcon,
  CardPaymentIconWhite,
  FourSquaresColor,
  FourSquaresColorWhite,
  UsersIcon,
  UsersIconWhite,
} from '../images/shapes';

import Structure from './Structure';

const testId = 'layout';

interface LayoutOrganizationProps {
  /**
   * Layout content
   */
  children?: ReactNode;
}

const LayoutOrganization = ({ children }: LayoutOrganizationProps) => {
  const { t } = useTranslation();
  const jwt = useToken();
  const { userData, userStorage, isAdmin } = useContext(UserContext);
  const [darkMode] = useDarkMode();

  const manageSubscriptionClick = useCallback(
    () => manageSubscription(jwt, userStorage?.data?.id, t),
    [jwt, t, userStorage?.data?.id],
  );

  const SIDEBAR_MENU_ITEMS = useMemo(() => {
    const menuItems: MenuItemChildProps[] = [
      {
        key: 'backToDashboard',
        icon: (
          <ArrowLeftIcon className="w-6 min-w-6 p-1 text-black dark:text-slate-200" />
        ),
        title: t('Global.BackToTheDashboard'),
        link: '/',
        titleClassName: 'text-red underline',
        className: `h-10 hover:bg-transparent`,
      },
    ];

    if (isAdmin) {
      menuItems.push({
        key: 'spaces-and-usage',
        icon: darkMode ? (
          <FourSquaresColorWhite className="w-6 min-w-6" />
        ) : (
          <FourSquaresColor className="w-6 min-w-6" />
        ),
        title: t('Spaces.SpacesAndUsage'),
        link: '/organization',
      });
      menuItems.push({
        key: 'users',
        icon: darkMode ? (
          <UsersIconWhite className="w-6 min-w-6" />
        ) : (
          <UsersIcon className="w-6 min-w-6" />
        ),
        title: t('Global.Users'),
        link: '/users',
      });

      const isBillingDisabled = !userData?.paymentClientId;
      const billingTooltip = isBillingDisabled
        ? t('AccountSettings.NoSubscription')
        : t('AccountSettings.ManageSubscriptionText') +
          ' ' +
          t('AccountSettings.ManageSubscriptionLinkText');

      const stripeURL = getStripeURL();
      if (stripeURL) {
        menuItems.push({
          key: 'billing',
          icon: darkMode ? (
            <CardPaymentIconWhite className="w-6 min-w-6" />
          ) : (
            <CardPaymentIcon className="w-6 min-w-6" />
          ),
          title: t('Global.Billing'),
          onClick: manageSubscriptionClick,
          tooltip: billingTooltip,
          disabled: isBillingDisabled,
          titleClassName: twMerge(
            'after:ml-2 after:content-["↗"] after:inline-block',
            !isBillingDisabled &&
              'group-hover:underline group-hover:after:no-underline',
          ),
        });
      }
    }

    return menuItems;
  }, [
    t,
    isAdmin,
    darkMode,
    userData?.paymentClientId,
    manageSubscriptionClick,
  ]);

  return (
    <Structure sidebarMenuItems={SIDEBAR_MENU_ITEMS} testId={testId}>
      {children ? children : <Outlet />}
    </Structure>
  );
};

export default LayoutOrganization;
